// karyawan.service — guard izin + orkestrasi simpan/suspend/hapus. Port saveKar/karSuspend/karDelete v0.18.
import { currentUser } from '@core/session';
import { verifyOwnPassword } from '@modules/auth/auth.service';
import { bisaUbah } from '@core/perms';
import { logAudit } from '@core/audit';
import { parseKomisiVal } from '@domain/format';
import type { Role } from '@core/types';
import * as repo from './karyawan.repo';

/** Deteksi unique-violation username (retry saat SPV bentrok cross-outlet tak terlihat RLS). */
const isUniqueUsernameError = (e: unknown): boolean =>
  /duplicate key|unique constraint|already exists/i.test((e as Error)?.message ?? '');

export const DEF_SCOPE: Record<string, string> =
  { Owner: 'Semua', Manager: 'Semua', SPV: 'Cabang', 'Admin Finance': 'Cabang', CS: 'Cabang', Teknisi: 'Sendiri' };
export const DIVISI: Role[] = ['Owner', 'Manager', 'SPV', 'Admin Finance', 'CS', 'Teknisi'];

/** Boleh atur hak/login (matriks penuh menyusul). */
export const canHak = (): boolean => { const u = currentUser(); return !!u && ['Owner', 'Manager'].includes(u.role); };

/** Boleh ubah karyawan k (undefined = boleh tambah baru). SPV hanya Teknisi/CS di cabangnya. */
export function canManageKar(k?: repo.KaryawanRow): boolean {
  const u = currentUser(); if (!u) return false;
  // Peran DAN Hak Akses (Pengaturan → 👔 Karyawan). Guard di service, bukan hanya tombol.
  if (!bisaUbah(k ? 'pengaturan.karyawan.ubah' : 'pengaturan.karyawan.tambah')) return false;
  if (['Owner', 'Manager'].includes(u.role)) return true;
  if (u.role === 'SPV') return !k || (['Teknisi', 'CS'].includes(k.role) && k.outlet === u.outlet);
  return false;
}

/** Boleh suspend/aktifkan (bukan diri sendiri, bukan Owner). */
export function canSuspendKar(k: repo.KaryawanRow): boolean {
  const u = currentUser();
  if (!bisaUbah('pengaturan.karyawan.suspend')) return false;
  if (!u || !k || k.id === u.id || k.role === 'Owner' || !['Owner', 'Manager', 'SPV'].includes(u.role)) return false;
  if (u.role === 'Owner') return true;
  if (u.role === 'Manager') return !['Owner', 'Manager'].includes(k.role);
  return k.outlet === u.outlet && !['Owner', 'Manager', 'SPV'].includes(k.role); // SPV → non-privileged, cabang sendiri
}

const slugUser = (n: string): string =>
  (n || 'user').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 12) || 'user';

export interface KarForm {
  nama: string; role: Role; outlet: string | null; level: string; wa: string; email: string;
  lahir: string; rekening: string; username: string; scope: string; aktif: boolean;
  komisiType: string; komisiVal: string; // skema komisi per-karyawan (privileged O/M) — v18 emp.komisiType/komisiVal
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Email WAJIB & valid & unik (dasar self-reset password). Disimpan lowercase. exceptId = baris yang diedit. */
async function emailData(raw: string, exceptId: number): Promise<string> {
  const email = raw.trim().toLowerCase();
  if (!email) throw new Error('Email wajib diisi (untuk reset password & akun login).');
  if (!EMAIL_RE.test(email)) throw new Error('Format email tidak valid (mis. nama@email.com).');
  if (await repo.emailTaken(email, exceptId || undefined)) throw new Error('Email sudah dipakai karyawan lain.');
  return email;
}

/** Simpan karyawan. `existing`=row lama (null jika baru). Privileged (Owner/Manager) atur penuh;
 *  SPV edit hanya field aman, atau tambah Teknisi baru cabang sendiri (username auto). */
export async function saveKar(id: number, f: KarForm, existing: repo.KaryawanRow | null): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const nama = f.nama.trim(); if (!nama) throw new Error('Nama wajib diisi.');
  // Larang menonaktifkan Owner / akun sendiri.
  if (existing && !f.aktif && (existing.role === 'Owner' || existing.id === u.id))
    throw new Error(existing.role === 'Owner' ? 'Owner tidak dapat disuspend.' : 'Tidak dapat menonaktifkan akun sendiri.');
  const extras = { lahir: f.lahir || null, rekening: f.rekening.trim() || null };

  if (canHak()) {
    // Jangan turunkan/ubah peran Owner terakhir (cegah nol-Owner / lockout).
    if (existing && existing.role === 'Owner' && f.role !== 'Owner' && (await repo.ownerCount()) <= 1)
      throw new Error('Owner terakhir tidak dapat diturunkan perannya.');
    const username = f.username.trim().toLowerCase();
    if (!username) throw new Error('Username wajib diisi (semua karyawan wajib login).');
    if (await repo.usernameTaken(username, id || undefined)) throw new Error('Username sudah dipakai.');
    const scope = f.scope || DEF_SCOPE[f.role] || 'Cabang';
    // Komisi per-karyawan: type valid → simpan (val 0 VALID = komisi nol, JANGAN jadi null yg fallback ke global). type kosong → null (pakai skema global).
    const kType = ['profit', 'omzet', 'nominal'].includes(f.komisiType) ? f.komisiType : null;
    // parse locale-ID (titik=ribuan, koma=desimal); val 0 VALID (komisi nol ≠ pakai skema global). type kosong → null.
    const kVal = kType ? parseKomisiVal(f.komisiVal) : null;
    const email = await emailData(f.email, id);
    const data = { nama, role: f.role, outlet: f.outlet, level: f.level || null, wa: f.wa.trim() || null, email, aktif: f.aktif, username, scope, komisi_type: kType, komisi_val: kVal, ...extras };
    if (id) await repo.updateKaryawan(id, data); else await repo.createKaryawan(data);
  } else if (id && existing) {
    // SPV edit karyawan yang sudah ada → HANYA field aman, tak pernah role/login/scope.
    if (!canManageKar(existing)) throw new Error('Tidak punya akses mengubah karyawan ini.');
    if (existing.aktif !== f.aktif && !canSuspendKar(existing)) throw new Error('Tidak punya akses suspend/aktifkan karyawan ini.');
    const email = await emailData(f.email, id);
    await repo.updateKaryawan(id, { nama, level: f.level || existing.level || 'Junior', wa: f.wa.trim() || null, email, aktif: f.aktif, ...extras });
  } else {
    // SPV tambah Teknisi baru di cabang sendiri (username auto-slug).
    if (u.role !== 'SPV') throw new Error('Tidak punya akses menambah karyawan.');
    const email = await emailData(f.email, 0);
    const base = slugUser(nama); let n = 2, username = base;
    while (await repo.usernameTaken(username)) { username = base + n; n++; } // probe (hanya RLS-visible = cabang sendiri)
    const payload = { nama, role: 'Teknisi' as Role, outlet: u.outlet, level: f.level || 'Junior', wa: f.wa.trim() || null, email, aktif: f.aktif, scope: 'Sendiri', ...extras };
    // Bentrok cross-outlet TAK terlihat RLS read-scope SPV → retry saat unique-violation (v18 lihat seluruh list & increment).
    for (let attempt = 0; attempt < 30; attempt++) {
      try { await repo.createKaryawan({ ...payload, username }); return; }
      catch (e) {
        if (attempt < 29 && isUniqueUsernameError(e)) { username = base + n; n++; continue; }
        throw e;
      }
    }
  }
}

export interface KomisiRow { id: number; type: string; val: string }
/** Simpan skema komisi BANYAK karyawan sekaligus (tab Komisi Pengaturan). O/M; RLS 0034 guard komisi.
 *  type kosong = pakai skema global (komisi_type=null). val 0 valid. Port saveKomisiSetting v0.18 (+opsi global). */
export async function saveKomisiBulk(rows: KomisiRow[]): Promise<number> {
  const u = currentUser();
  if (!u || !['Owner', 'Manager'].includes(u.role)) throw new Error('Skema komisi hanya Owner/Manager.');
  for (const r of rows) {
    const type = ['profit', 'omzet', 'nominal'].includes(r.type) ? r.type : null;
    const val = type ? parseKomisiVal(r.val) : null;
    await repo.updateKaryawan(r.id, { komisi_type: type, komisi_val: val });
  }
  return rows.length;
}

export async function suspendKar(k: repo.KaryawanRow): Promise<boolean> {
  if (!canSuspendKar(k)) throw new Error('Tidak punya akses suspend/aktifkan karyawan ini.');
  const u = currentUser()!;
  const next = k.aktif === false; // aktifkan bila sedang nonaktif
  await repo.updateKaryawan(k.id, { aktif: next, suspended_by: next ? null : u.nama, suspended_at: next ? null : new Date().toISOString() });
  await logAudit({ action: next ? 'EMPLOYEE_ACTIVATE' : 'EMPLOYEE_SUSPEND', module: 'employee', refId: String(k.id), meta: { nama: k.nama, role: k.role } });
  return next;
}

export async function deleteKar(k: repo.KaryawanRow): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'Owner') throw new Error('Hapus karyawan hanya untuk Owner.');
  if (k.id === u.id) throw new Error('Tidak dapat menghapus akun sendiri.');
  if (k.role === 'Owner' && (await repo.ownerCount()) <= 1) throw new Error('Owner terakhir tidak dapat dihapus.');
  await repo.removeKaryawan(k.id);
  await logAudit({ action: 'EMPLOYEE_DELETE', module: 'employee', refId: String(k.id), meta: { nama: k.nama, role: k.role } });
}

/**
 * Buat / reset akun login karyawan. Guard di sini = lapis pertama (UX);
 * Edge Function memverifikasi ulang peran dari DB — klien tak bisa mengaku-ngaku Owner.
 * Password sementara TIDAK di-audit (rahasia); yang dicatat hanya jejak aksinya.
 */
export async function provisionAkun(k: repo.KaryawanRow, action: 'create' | 'reset', password?: string, konfirmasi?: string): Promise<repo.AkunHasil> {
  // Kelola akun login (kredensial) = izin `kelola_akun` (Owner full; Manager read-only = tak boleh).
  // Ditegakkan ULANG di Edge Function (Owner-only) — ini lapis app + UX.
  if (!bisaUbah('pengaturan.karyawan.kelola_akun')) throw new Error('Hanya Owner yang boleh membuat/mereset akun login.');
  // Bukti kedua. Membuat atau mereset akun login berarti membuat pijakan masuk yang bertahan
  // setelah sesi ini berakhir — konsekuensinya lebih panjang dari kebanyakan aksi lain, dan
  // sampai sekarang peran saja sudah cukup untuk melakukannya. Pola sama dengan hapus tiket,
  // Reset Data, dan restore backup.
  await verifyOwnPassword(konfirmasi ?? '', 'Password Anda salah — akun tidak dibuat/direset.');
  // Akun Owner hanya boleh disentuh Owner (cegah Manager me-reset password Owner lalu masuk sebagai Owner).
  // Ditegakkan ULANG di Edge Function — ini hanya lapis UX.
  if (k.role === 'Owner' && currentUser()?.role !== 'Owner') throw new Error('Hanya Owner yang boleh mengelola akun Owner.');
  if (!k.username?.trim()) throw new Error('Isi Username dulu di data karyawan, baru buat akunnya.');
  if (k.role === 'Supplier') throw new Error('Akun Supplier dibuat dari Pengaturan → Supplier (tombol 🔑), bukan di sini.');
  // Password admin-set opsional: guard panjang di sini (UX) + ditegakkan ULANG di Edge Function.
  const pass = (password ?? '').trim();
  if (pass && pass.length < 6) throw new Error('Password minimal 6 karakter.');
  // Audit TIDAK ditulis di sini: audit sisi-klien bisa dilewati (panggil Edge Function langsung /
  // blokir request audit_log). Pencatatannya ada DI DALAM Edge Function, tak bisa dihindari.
  return repo.provisionAccount(k.id, action, pass || undefined);
}

/**
 * Isi email banyak karyawan sekaligus (untuk data lama yang belum punya email — email kini wajib).
 * Divalidasi seluruhnya DULU baru disimpan: kalau satu baris salah, tak ada yang tersimpan setengah
 * jalan sehingga Owner tak perlu menebak mana yang sudah masuk. Mengembalikan jumlah yang tersimpan.
 */
export async function simpanEmailMassal(rows: { id: number; email: string }[]): Promise<number> {
  if (!bisaUbah('pengaturan.karyawan.ubah')) throw new Error('Tidak punya akses mengubah karyawan.');
  const isi = rows.map((r) => ({ id: r.id, email: r.email.trim().toLowerCase() })).filter((r) => r.email);
  if (!isi.length) throw new Error('Belum ada email yang diisi.');
  const seen = new Set<string>();
  for (const r of isi) {
    if (!EMAIL_RE.test(r.email)) throw new Error(`Format email tidak valid: ${r.email}`);
    if (seen.has(r.email)) throw new Error(`Email ganda dalam daftar ini: ${r.email}`);
    seen.add(r.email);
    if (await repo.emailTaken(r.email, r.id)) throw new Error(`Email sudah dipakai karyawan lain: ${r.email}`);
  }
  for (const r of isi) await repo.updateKaryawan(r.id, { email: r.email });
  return isi.length;
}

/** Username default dari nama supplier: huruf/angka saja, tanpa spasi/@ (maks 20). */
export const supUsername = (nama: string): string => nama.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 20) || 'supplier';

/**
 * Buat / reset akun login SUPPLIER (dari Pengaturan → Supplier).
 * Membuat baris employees(role='Supplier', supplier_id) bila belum ada, lalu provision auth.
 * Guard Owner/Manager (dilapis ulang di Edge Function). Untuk 'reset', username diabaikan (baris sudah ada).
 */
export async function provisionSupplierAkun(supplierId: number, nama: string, username: string, action: 'create' | 'reset'): Promise<repo.AkunHasil> {
  if (!canHak()) throw new Error('Hanya Owner/Manager yang boleh mengelola akun login.');
  const u = username.trim().toLowerCase();
  if (!u) throw new Error('Isi username dulu.');
  if (!/^[a-z0-9]+$/.test(u)) throw new Error('Username hanya boleh huruf & angka (tanpa spasi, @, atau simbol).');
  const empId = await repo.ensureSupplierEmployee(supplierId, nama, u);
  return repo.provisionAccount(empId, action);
}
